import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Drawer, Box, Typography, IconButton, TextField, ToggleButtonGroup, ToggleButton,
  Chip, Collapse, Tooltip, Button, Divider, alpha, useTheme, useMediaQuery,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import DescriptionIcon from '@mui/icons-material/Description';
import ShareIcon from '@mui/icons-material/Share';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import SmartphoneIcon from '@mui/icons-material/Smartphone';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ErrorIcon from '@mui/icons-material/Error';
import WarningIcon from '@mui/icons-material/Warning';
import InfoIcon from '@mui/icons-material/Info';
import RouteIcon from '@mui/icons-material/Explore';
import ApiIcon from '@mui/icons-material/Cloud';
import TimerIcon from '@mui/icons-material/Timer';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { debugService, DebugEvent, EventLevel } from '../../services/debugService';

const LEVEL_COLORS: Record<EventLevel, string> = { error: '#ef4444', warn: '#f59e0b', info: '#6b7280' };
const LEVEL_ICONS: Record<EventLevel, React.ReactNode> = {
  error: <ErrorIcon sx={{ fontSize: 14, color: '#ef4444' }} />,
  warn: <WarningIcon sx={{ fontSize: 14, color: '#f59e0b' }} />,
  info: <InfoIcon sx={{ fontSize: 14, color: '#6b7280' }} />,
};

function EventRow({ event }: { event: DebugEvent }) {
  const [open, setOpen] = useState(false);
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const time = new Date(event.ts).toLocaleTimeString('en-US', { hour12: false, hour: '2-digit', minute: '2-digit', second: '2-digit' });
  const ms = event.ts.split('.')[1]?.slice(0, 3) || '000';

  const summary = useMemo(() => {
    const d = event.data;
    switch (event.type) {
      case 'api_request': return `${d.method} ${d.url}`;
      case 'api_response': return `${d.status} ${d.method} ${d.url} (${d.durationMs}ms)`;
      case 'api_error': return `${d.method} ${d.url} — ${d.message}`;
      case 'console_error':
      case 'console_warn': return d.args?.join(' ').slice(0, 120) || '';
      case 'unhandled_error':
      case 'unhandled_rejection': return d.message?.slice(0, 120) || '';
      case 'react_error': return d.message?.slice(0, 120) || '';
      case 'action': return d.action || '';
      case 'ai_workflow': return d.action || 'AI workflow event';
      case 'route_change': return `→ ${d.path}${d.search || ''}`;
      case 'mobile_nav': return `[${d.module}] ${d.action}`;
      case 'mobile_lifecycle': return `[${d.module}] ${d.action}`;
      case 'mobile_gesture': return `[${d.module}] ${d.gesture}`;
      case 'mobile_data': return `[${d.module}] ${d.action}`;
      case 'mobile_interaction': return `[${d.module}] ${d.action}`;
      case 'mobile_render': return `[${d.module}] ${d.issue}`;
      case 'mobile_perf': return `[${d.module}] ${d.metric} (${d.durationMs}ms)`;
      default: return JSON.stringify(d).slice(0, 100);
    }
  }, [event]);

  return (
    <Box sx={{
      borderBottom: '1px solid', borderColor: 'divider',
      bgcolor: event.level === 'error' ? alpha(LEVEL_COLORS.error, isDark ? 0.08 : 0.04) : 'transparent',
      '&:hover': { bgcolor: isDark ? alpha('#fff', 0.03) : alpha('#000', 0.02) },
    }}>
      <Box
        onClick={() => setOpen(!open)}
        sx={{ display: 'flex', alignItems: 'center', gap: 0.75, px: 1.5, py: 0.75, cursor: 'pointer', minHeight: 32 }}
      >
        {LEVEL_ICONS[event.level]}
        <Typography sx={{ fontSize: 10, fontFamily: 'monospace', color: 'text.disabled', whiteSpace: 'nowrap', minWidth: 72 }}>
          {time}.{ms}
        </Typography>
        <Chip label={event.type.replace(/_/g, ' ')} size="small" sx={{
          height: 18, fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5,
          bgcolor: alpha(LEVEL_COLORS[event.level], 0.12), color: LEVEL_COLORS[event.level],
        }} />
        <Typography sx={{ fontSize: 12, fontFamily: 'monospace', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {summary}
        </Typography>
        {open ? <ExpandLessIcon sx={{ fontSize: 14, color: 'text.disabled' }} /> : <ExpandMoreIcon sx={{ fontSize: 14, color: 'text.disabled' }} />}
      </Box>
      <Collapse in={open}>
        <Box sx={{ px: 2, pb: 1.5, pt: 0.5 }}>
          <pre style={{
            margin: 0, fontSize: 11, lineHeight: 1.5, fontFamily: 'monospace',
            whiteSpace: 'pre-wrap', wordBreak: 'break-word', maxHeight: 300, overflow: 'auto',
            color: theme.palette.text.secondary,
          }}>
            {JSON.stringify(event.data, null, 2)}
          </pre>
        </Box>
      </Collapse>
    </Box>
  );
}

interface Props {
  open: boolean;
  onClose: () => void;
}

type CategoryFilter = 'all' | 'mobile' | 'api' | 'errors' | 'actions' | 'routes' | 'ai';

const AI_ROADMAP_ITEMS = [
  'Approval gates',
  'Run history',
  'Async sequencing',
  'Publish validation',
  'Live sandbox config',
  'Workflow usage map',
  'Usage metering',
  'Explicit agent selection',
  'Output schemas',
  'Knowledge citations',
  'Debug observability',
];

const DebugPanel: React.FC<Props> = ({ open, onClose }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery('(max-width:767.95px)');
  const [events, setEvents] = useState<readonly DebugEvent[]>([]);
  const [search, setSearch] = useState('');
  const [levelFilter, setLevelFilter] = useState<EventLevel | 'all'>('all');
  const [categoryFilter, setCategoryFilter] = useState<CategoryFilter>('all');
  const [copySuccess, setCopySuccess] = useState(false);
  const [showSummary, setShowSummary] = useState(true);

  useEffect(() => {
    const sync = () => setEvents([...debugService.getEvents()]);
    sync();
    return debugService.subscribe(sync);
  }, []);

  const filtered = useMemo(() => {
    let result = [...events];
    if (levelFilter !== 'all') result = result.filter(e => e.level === levelFilter);
    if (categoryFilter === 'mobile') result = result.filter(e => e.type.startsWith('mobile_'));
    else if (categoryFilter === 'api') result = result.filter(e => e.type.startsWith('api_'));
    else if (categoryFilter === 'errors') result = result.filter(e => e.level === 'error');
    else if (categoryFilter === 'actions') result = result.filter(e => e.type === 'action');
    else if (categoryFilter === 'routes') result = result.filter(e => e.type === 'route_change');
    else if (categoryFilter === 'ai') result = result.filter(e => e.type === 'ai_workflow' || String(e.data?.action || '').startsWith('eng_ai_agent') || String(e.data?.url || '').includes('/ai-agent'));
    if (search.trim()) {
      const q = search.toLowerCase();
      result = result.filter(e => {
        const str = `${e.type} ${JSON.stringify(e.data)}`.toLowerCase();
        return str.includes(q);
      });
    }
    return result.reverse();
  }, [events, levelFilter, categoryFilter, search]);

  const errorCount = useMemo(() => events.filter(e => e.level === 'error').length, [events]);
  const warnCount = useMemo(() => events.filter(e => e.level === 'warn').length, [events]);
  const mobileCount = useMemo(() => events.filter(e => e.type.startsWith('mobile_')).length, [events]);
  const apiCount = useMemo(() => events.filter(e => e.type.startsWith('api_')).length, [events]);
  const actionCount = useMemo(() => events.filter(e => e.type === 'action').length, [events]);
  const routeCount = useMemo(() => events.filter(e => e.type === 'route_change').length, [events]);
  const aiCount = useMemo(() => events.filter(e => e.type === 'ai_workflow' || String(e.data?.action || '').startsWith('eng_ai_agent') || String(e.data?.url || '').includes('/ai-agent')).length, [events]);

  const handleExport = useCallback(() => debugService.downloadExport(), []);
  const handleExportMd = useCallback(() => debugService.downloadMarkdownExport(), []);
  const handleClear = useCallback(() => debugService.clear(), []);
  const handleCopy = useCallback(async () => {
    const ok = await debugService.copyExportToClipboard();
    if (ok) { setCopySuccess(true); setTimeout(() => setCopySuccess(false), 2000); }
  }, []);
  const handleShare = useCallback(async () => { await debugService.shareExport(); }, []);

  const sessionDuration = useMemo(() => {
    const ms = Date.now() - debugService.sessionStartTime;
    const s = Math.floor(ms / 1000);
    const m = Math.floor(s / 60);
    if (m > 0) return `${m}m ${s % 60}s`;
    return `${s}s`;
  }, [events]);

  return (
    <Drawer
      anchor={isMobile ? 'bottom' : 'right'}
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: isMobile
          ? { width: '100%', height: '100vh', maxHeight: '100vh', borderRadius: 0 }
          : { width: { xs: '100%', sm: 560 } },
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', pt: isMobile ? 'var(--safe-area-top, 0px)' : 0, pb: isMobile ? 'var(--safe-area-bottom, 0px)' : 0 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, px: 1.5, py: 1, borderBottom: '1px solid', borderColor: 'divider', flexWrap: 'wrap' }}>
          <Typography variant="subtitle1" fontWeight={700} sx={{ flex: 1, fontSize: isMobile ? '0.9rem' : undefined }}>Dev Console</Typography>
          {isMobile && !!navigator.share && (
            <Tooltip title="Share debug log"><IconButton size="small" onClick={handleShare}><ShareIcon sx={{ fontSize: 16 }} /></IconButton></Tooltip>
          )}
          <Tooltip title={copySuccess ? 'Copied!' : 'Copy to clipboard'}>
            <IconButton size="small" onClick={handleCopy}><ContentCopyIcon sx={{ fontSize: 16, color: copySuccess ? '#22c55e' : undefined }} /></IconButton>
          </Tooltip>
          <Tooltip title="Clear all"><IconButton size="small" onClick={handleClear}><DeleteSweepIcon sx={{ fontSize: 16 }} /></IconButton></Tooltip>
          <IconButton size="small" onClick={onClose}><CloseIcon sx={{ fontSize: 16 }} /></IconButton>
        </Box>

        {/* Export bar */}
        <Box sx={{
          display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 1,
          borderBottom: '1px solid', borderColor: 'divider',
          bgcolor: alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.08 : 0.04),
        }}>
          <Button
            variant="contained" size="small"
            startIcon={<FileDownloadIcon />}
            onClick={handleExport}
            sx={{ textTransform: 'none', fontWeight: 700, fontSize: 12, py: 0.5 }}
          >
            Export JSON
          </Button>
          <Button
            variant="outlined" size="small"
            startIcon={<DescriptionIcon />}
            onClick={handleExportMd}
            sx={{ textTransform: 'none', fontWeight: 600, fontSize: 12, py: 0.5 }}
          >
            Export Markdown
          </Button>
          <Box sx={{ flex: 1 }} />
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10, fontFamily: 'monospace' }}>
            {events.length}/{5000}
          </Typography>
        </Box>

        {/* Summary section */}
        <Box
          onClick={() => setShowSummary(!showSummary)}
          sx={{
            display: 'flex', alignItems: 'center', gap: 0.5, px: 1.5, py: 0.75,
            borderBottom: '1px solid', borderColor: 'divider', cursor: 'pointer',
            '&:hover': { bgcolor: alpha('#000', 0.02) },
          }}
        >
          <Typography sx={{ fontSize: 11, fontWeight: 700, flex: 1 }}>Session Summary</Typography>
          {showSummary ? <ExpandLessIcon sx={{ fontSize: 14 }} /> : <ExpandMoreIcon sx={{ fontSize: 14 }} />}
        </Box>
        <Collapse in={showSummary}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, px: 1.5, py: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
            <SummaryChip icon={<TimerIcon />} label={sessionDuration} sublabel="Duration" />
            <SummaryChip icon={<ApiIcon />} label={String(apiCount)} sublabel="API calls" />
            <SummaryChip icon={<SmartToyIcon />} label={String(aiCount)} sublabel="AI workflow" color={aiCount > 0 ? '#f76100' : undefined} />
            <SummaryChip icon={<RouteIcon />} label={String(routeCount)} sublabel="Routes" />
            <SummaryChip icon={<ErrorIcon />} label={String(errorCount)} sublabel="Errors" color={errorCount > 0 ? '#ef4444' : undefined} />
            <SummaryChip icon={<WarningIcon />} label={String(warnCount)} sublabel="Warnings" color={warnCount > 0 ? '#f59e0b' : undefined} />
          </Box>
        </Collapse>

        {/* Category filter chips */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, px: 1.5, py: 0.75, borderBottom: '1px solid', borderColor: 'divider', overflowX: 'auto' }}>
          {([
            { key: 'all' as CategoryFilter, label: 'All' },
            { key: 'actions' as CategoryFilter, label: `Actions (${actionCount})` },
            { key: 'api' as CategoryFilter, label: `API (${apiCount})` },
            { key: 'ai' as CategoryFilter, label: `AI (${aiCount})` },
            { key: 'routes' as CategoryFilter, label: `Routes (${routeCount})` },
            { key: 'errors' as CategoryFilter, label: `Errors (${errorCount})` },
            { key: 'mobile' as CategoryFilter, label: `Mobile (${mobileCount})` },
          ]).map(cat => (
            <Chip
              key={cat.key}
              label={cat.label}
              size="small"
              variant={categoryFilter === cat.key ? 'filled' : 'outlined'}
              onClick={() => setCategoryFilter(cat.key)}
              icon={cat.key === 'mobile' ? <SmartphoneIcon sx={{ fontSize: '14px !important' }} /> : cat.key === 'ai' ? <SmartToyIcon sx={{ fontSize: '14px !important' }} /> : undefined}
              sx={{
                height: 24, fontSize: 11, fontWeight: categoryFilter === cat.key ? 700 : 500,
                ...(cat.key === 'errors' && errorCount > 0 && categoryFilter === cat.key ? { bgcolor: alpha('#ef4444', 0.15), color: '#ef4444', borderColor: '#ef4444' } : {}),
                ...(cat.key === 'mobile' && categoryFilter === cat.key ? { bgcolor: alpha('#3b82f6', 0.15), color: '#3b82f6', borderColor: '#3b82f6' } : {}),
                ...(cat.key === 'ai' && categoryFilter === cat.key ? { bgcolor: alpha('#f76100', 0.15), color: '#f76100', borderColor: '#f76100' } : {}),
              }}
            />
          ))}
        </Box>

        {/* AI workflow diagnostic progress */}
        {categoryFilter === 'ai' && (
          <Box sx={{ px: 1.5, py: 1, borderBottom: '1px solid', borderColor: 'divider', bgcolor: alpha('#f76100', theme.palette.mode === 'dark' ? 0.08 : 0.04) }}>
            <Typography sx={{ fontSize: 11, fontWeight: 800, mb: 0.75 }}>AI Workflow Roadmap Diagnostics</Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {AI_ROADMAP_ITEMS.map(item => (
                <Chip
                  key={item}
                  icon={<CheckCircleIcon sx={{ fontSize: '13px !important' }} />}
                  label={item}
                  size="small"
                  variant="outlined"
                  sx={{ height: 22, fontSize: 10, bgcolor: alpha('#f76100', 0.06) }}
                />
              ))}
            </Box>
          </Box>
        )}

        {/* Search + level filters */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, px: 1.5, py: 0.75, borderBottom: '1px solid', borderColor: 'divider' }}>
          <TextField
            size="small" placeholder="Search events..." value={search}
            onChange={e => setSearch(e.target.value)}
            sx={{ flex: 1, '& .MuiInputBase-input': { fontSize: 12, py: 0.75 } }}
          />
          <ToggleButtonGroup
            size="small" exclusive value={levelFilter}
            onChange={(_e, v) => { if (v !== null) setLevelFilter(v); }}
          >
            <ToggleButton value="all" sx={{ fontSize: 10, px: 0.75, py: 0.5, textTransform: 'none' }}>All</ToggleButton>
            <ToggleButton value="error" sx={{ fontSize: 10, px: 0.75, py: 0.5, textTransform: 'none', color: '#ef4444' }}>Err</ToggleButton>
            <ToggleButton value="warn" sx={{ fontSize: 10, px: 0.75, py: 0.5, textTransform: 'none', color: '#f59e0b' }}>Warn</ToggleButton>
          </ToggleButtonGroup>
        </Box>

        {/* Event list */}
        <Box sx={{ flex: 1, overflow: 'auto', WebkitOverflowScrolling: 'touch' }}>
          {filtered.length === 0 ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', gap: 1, px: 3 }}>
              <Typography variant="body2" color="text.disabled" textAlign="center">
                {events.length === 0 ? 'No events captured yet. Use the app to generate events.' : 'No events match your filter.'}
              </Typography>
            </Box>
          ) : (
            filtered.map(event => <EventRow key={event.id} event={event} />)
          )}
        </Box>

        {/* Footer */}
        <Divider />
        <Box sx={{ px: 1.5, py: 0.75, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
          <Typography variant="caption" color="text.disabled" sx={{ fontSize: 10 }}>
            {events.length}/5000 — {window.innerWidth}x{window.innerHeight} — {filtered.length} shown
          </Typography>
          <Button size="small" variant="outlined" startIcon={isMobile && !!navigator.share ? <ShareIcon /> : <FileDownloadIcon />} onClick={isMobile && !!navigator.share ? handleShare : handleExport} sx={{ fontSize: 10, textTransform: 'none', py: 0.25 }}>
            {isMobile && !!navigator.share ? 'Share' : 'Export'}
          </Button>
        </Box>
      </Box>
    </Drawer>
  );
};

function SummaryChip({ icon, label, sublabel, color }: { icon: React.ReactNode; label: string; sublabel: string; color?: string }) {
  const theme = useTheme();
  const c = color || theme.palette.text.secondary;
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, px: 1, py: 0.5, borderRadius: 1, bgcolor: alpha(c, 0.08), minWidth: 70 }}>
      {React.cloneElement(icon as React.ReactElement, { sx: { fontSize: 14, color: c } })}
      <Box>
        <Typography sx={{ fontSize: 13, fontWeight: 800, fontFamily: 'monospace', color: c, lineHeight: 1 }}>{label}</Typography>
        <Typography sx={{ fontSize: 9, color: 'text.disabled', lineHeight: 1.2 }}>{sublabel}</Typography>
      </Box>
    </Box>
  );
}

export default DebugPanel;
